import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output, Renderer2, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {UpdateMasterModel} from "../../models/UpdateMaster-model";
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {AllMastersDatabaseService} from "../../service/all-masters-database.service";
import {Observable, Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {AddDialogComponent} from "../add-dialog/add-dialog.component";
import {CorrectEcuProducersModel, EcuBuildNameModel} from "../../models/EcuBuildName-model";
import {Clipboard} from '@angular/cdk/clipboard'
import {AddEcuProducerDialogComponent} from "../add-ecu-producer-dialog/add-ecu-producer-dialog.component";
import {EcuNamesService} from "../../service/Ecu-names.service";
import {EcuProducersService} from "../../service/ecu-producers.service";
import {VehicleDataService} from "../../service/vehicleData.service";
import {NotificationService} from "../../service/Notification.service";


@Component({
    selector: 'app-edit-dialog',
    templateUrl: './edit-master-dialog.component.html',
    styleUrls: ['./edit-master-dialog.component.css']
})
export class EditMasterDialogComponent implements OnInit, OnDestroy {


    @Output() masterEcuBuildChangeEmitter = new EventEmitter<string>();
    public activeTab = signal<'build' | 'details'>('details');
    public contentToShow: UpdateMasterModel;
    public ecuBuild: EcuBuildNameModel;
    public ecuProducer: CorrectEcuProducersModel;

    public ecuBuildSearchQuery: string = '';
    public prevEcuBuildState: string;

    public ecuProducerOptions = signal<string[]>([]);
    public vehicleProducerOptions = signal<string[]>([]);
    public vehicleTypeOptions = signal<string[]>([]);

    private allVehicleProducers: string[] = [];
    private allVehicleTypes: string[] = [];

    private ecuProducerSearch$ = new Subject<string>();
    private vehicleProducerSearch$ = new Subject<string>();
    private vehicleTypeSearch$ = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private clipboard: Clipboard,
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<EditMasterDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            id: number,
            text: string,
            content: UpdateMasterModel
        },
        private allMasterService: AllMastersDatabaseService,
        private ecuNamesService: EcuNamesService,
        private ecuProducersService: EcuProducersService,
        private vehicleDataService: VehicleDataService,
        private notificationService: NotificationService,
        private renderer: Renderer2
    ) {
        this.getEcuName();
    }

    updateMasterItemForm = new FormGroup({
        ecuUse: new FormControl(''),
        ecuProducer: new FormControl('', Validators.required),
        ecuBuild: new FormControl('', [Validators.required], [this.ecuBuildValidator.bind(this)]),
        ecuProdNr: new FormControl(''),
        ecuSoftwareVersion: new FormControl(''),
        ecuSoftwareVersionVersion: new FormControl(''),
        engineName: new FormControl(''),
        engineDisplacement: new FormControl(''),
        ecuStgNr: new FormControl(''),
        ecuProcessor: new FormControl(''),
        engineType: new FormControl('', Validators.required),
        engineMaxTorque: new FormControl(''),
        outputPs: new FormControl(''),
        outputKw: new FormControl(''),
        engineTransmission: new FormControl(''),
        additionalInfo: new FormControl(''),
        vehicleModel: new FormControl(''),
        vehicleModelYear: new FormControl(''),
        vehicleProducer: new FormControl(''),
        vehicleSeries: new FormControl(''),
        uploadedBy: new FormControl(''),
        vehicleType: new FormControl(''),
        vehicleBuild: new FormControl(''),
        vehicleCharacteristic: new FormControl(''),
        engineProducer: new FormControl(''),
        engineEmission: new FormControl(''),
        engineGear: new FormControl(''),
        projectCreatedBy: new FormControl(''),
        projectChangedBy: new FormControl(''),
        readingHardware: new FormControl(''),
        projectComment: new FormControl(''),
        versionComment: new FormControl(''),
        softwareNumber: new FormControl(''),
        projectType: new FormControl(''),
    })

    ngOnInit() {
        this.contentToShow = this.data['content'];
        this.editFormValue();
        this.updateMasterItemForm.get('ecuBuild').markAsTouched();
        this.updateMasterItemForm.get('ecuBuild').setErrors({'valid': true});
        this.updateMasterItemForm.get('ecuProducer').markAsTouched();
        this.updateMasterItemForm.get('ecuBuild').markAsTouched();
        this.updateMasterItemForm.get('engineType').markAsTouched();
        this.updateMasterItemForm.get('engineType').setErrors({'valid': true});

        this.initEcuProducerAutocomplete();
        this.initVehicleProducerAutocomplete();
        this.initVehicleTypeAutocomplete();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }


    get requiredFieldsFilled(): boolean {
        return this.updateMasterItemForm.valid;
    }

    getWinOlsToClipboard() {
        this.clipboard.copy(this.data['text']);
    }

    getEcuName() {
        this.updateMasterItemForm.get('ecuBuild').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(response => {
                this.ecuBuildSearchQuery = response ?? '';
            });
    }

    private initEcuProducerAutocomplete() {
        this.ecuProducersService.getAllCorrectEcuProducers();

        this.ecuProducerSearch$
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(searchTerm => {
                const all = this.ecuProducersService.correctEcuProducersList() || [];
                const trimmed = (searchTerm || '').trim().toLowerCase();
                this.ecuProducerOptions.set(
                    trimmed
                        ? all.filter(item => item.toLowerCase().includes(trimmed))
                        : all
                );
            });

        this.updateMasterItemForm.get('ecuProducer').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => this.ecuProducerSearch$.next(value ?? ''));
    }

    private initVehicleProducerAutocomplete() {
        this.vehicleDataService.getCorrectVehicleProducers().subscribe({
            next: response => {
                this.allVehicleProducers = response.body || [];
                this.vehicleProducerOptions.set(this.allVehicleProducers);
            },
            error: err => console.error('Vehicle producer fetch failed', err)
        });

        this.vehicleProducerSearch$
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(searchTerm => {
                const trimmed = (searchTerm || '').trim().toLowerCase();
                this.vehicleProducerOptions.set(
                    trimmed
                        ? this.allVehicleProducers.filter(item => item.toLowerCase().includes(trimmed))
                        : this.allVehicleProducers
                );
            });

        this.updateMasterItemForm.get('vehicleProducer').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => this.vehicleProducerSearch$.next(value ?? ''));
    }

    private initVehicleTypeAutocomplete() {
        this.vehicleDataService.getCorrectVehicleTypes().subscribe({
            next: response => {
                this.allVehicleTypes = response.body || [];
                this.vehicleTypeOptions.set(this.allVehicleTypes);
            },
            error: err => console.error('Vehicle type fetch failed', err)
        });

        this.vehicleTypeSearch$
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(searchTerm => {
                const trimmed = (searchTerm || '').trim().toLowerCase();
                this.vehicleTypeOptions.set(
                    trimmed
                        ? this.allVehicleTypes.filter(item => item.toLowerCase().includes(trimmed))
                        : this.allVehicleTypes
                );
            });

        this.updateMasterItemForm.get('vehicleType').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => this.vehicleTypeSearch$.next(value ?? ''));
    }

    editFormValue() {
        this.prevEcuBuildState = this.contentToShow.ecuBuild;
        this.updateMasterItemForm.setValue({
            ecuUse: this.contentToShow.ecuUse,
            ecuProducer: this.contentToShow.ecuProducer,
            ecuBuild: this.contentToShow.ecuBuild,
            ecuProdNr: this.contentToShow.ecuProdNr,
            ecuSoftwareVersion: this.contentToShow.ecuSoftwareVersion,
            ecuSoftwareVersionVersion: this.contentToShow.ecuSoftwareVersionVersion,
            engineName: this.contentToShow.engineName,
            engineDisplacement: this.contentToShow.engineDisplacement,
            ecuStgNr: this.contentToShow.ecuStgNr,
            ecuProcessor: this.contentToShow.ecuProcessor,
            engineType: this.contentToShow.engineType,
            engineMaxTorque: this.contentToShow.engineMaxTorque,
            outputPs: this.contentToShow.outputPs,
            outputKw: this.contentToShow.outputKw,
            engineTransmission: this.contentToShow.engineTransmission,
            additionalInfo: this.contentToShow.additionalInfo,
            vehicleModel: this.contentToShow.vehicleModel,
            vehicleModelYear: this.contentToShow.vehicleModelYear,
            vehicleProducer: this.contentToShow.vehicleProducer,
            vehicleSeries: this.contentToShow.vehicleSeries,
            uploadedBy: this.contentToShow.uploadedBy,
            vehicleType: this.contentToShow.vehicleType,
            vehicleBuild: this.contentToShow.vehicleBuild,
            vehicleCharacteristic: this.contentToShow.vehicleCharacteristic,
            engineProducer: this.contentToShow.engineProducer,
            engineEmission: this.contentToShow.engineEmission,
            engineGear: this.contentToShow.engineGear,
            projectCreatedBy: this.contentToShow.projectCreatedBy,
            projectChangedBy: this.contentToShow.projectChangedBy,
            readingHardware: this.contentToShow.readingHardware,
            projectComment: this.contentToShow.projectComment,
            versionComment: this.contentToShow.versionComment,
            softwareNumber: this.contentToShow.softwareNumber,
            projectType: this.contentToShow.projectType,
        })
    }

    ecuBuildValidator(control: AbstractControl): Observable<ValidationErrors | null> {
        return this.allMasterService.checkIfEcuIsValid(control.value);
    }

    onEcuBuildChange() {
        const ecuBuildValue = this.updateMasterItemForm.get('ecuBuild')?.value;
        this.masterEcuBuildChangeEmitter.emit(ecuBuildValue);
    }

    confirmedOption() {
        this.onEcuBuildChange();
        this.dialogRef.close({
            closed: true,
            content: this.updateMasterItemForm.value,
            prevEcuBuildState: this.prevEcuBuildState
        });
    }

    canceledOption() {
        this.dialogRef.close({closed: false});
    }

    focusOnDialogElement() {
        const firstDialogElement = this.renderer.selectRootElement('#firstDialogElement');
        if (firstDialogElement) {
            firstDialogElement.focus();
        }
    }

    focusOnEcuProducerElement() {
        const firstDialogElement = this.renderer.selectRootElement('#secondDialogElement');
        if (firstDialogElement) {
            firstDialogElement.focus();
        }
    }

    addNewEcu() {
        this.dialog.open(AddDialogComponent, {
            width: "560px",
            panelClass: "form-dialog-v3-panel",
            data: {ecuBuildValue: this.updateMasterItemForm.get('ecuBuild').value}
        }).afterClosed().subscribe((value: {
            closed: boolean,
            ecu: EcuBuildNameModel
        }) => {
            if (value?.closed && value.ecu) {
                this.updateMasterItemForm.patchValue({
                    ecuBuild: value.ecu.correctEcuBuildNames,
                    ecuProducer: value.ecu.correctEcuProducer
                })
                this.ecuBuild = value.ecu;
                this.focusOnDialogElement();
            }
        });
    }

    getCorrectEcuName($event: any) {
        const ecuBuildValue = $event.valueOf();
        this.updateMasterItemForm.patchValue({
            ecuBuild: ecuBuildValue
        });
        this.ecuNamesService.getValidEcuProducerByEcuBuild(ecuBuildValue).subscribe(
            (response) => {
                this.updateMasterItemForm.patchValue({
                    ecuProducer: response.body["ecuProducer"]
                });
            },
            (error) => {
                this.notificationService.showErrorMessage(`${error.error.message}`, "Error occurred!");
            }
        );
    }

    addNewEcuProducer() {
        this.dialog.open(AddEcuProducerDialogComponent, {
            width: "520px",
            panelClass: "form-dialog-v3-panel",
            data: {ecuProducerValue: this.updateMasterItemForm.get('ecuProducer').value}
        }).afterClosed().subscribe((value: {
            closed: boolean,
            ecuProducer: CorrectEcuProducersModel
        }) => {
            if (value?.closed && value.ecuProducer) {
                this.updateMasterItemForm.patchValue({
                    ecuProducer: value.ecuProducer.correctEcuProducer
                })
                this.ecuProducer = value.ecuProducer;
                this.ecuProducerOptions.set([value.ecuProducer.correctEcuProducer]);
                this.focusOnEcuProducerElement()
            }
        });
    }
}
